import { ArrowsUpDownIcon } from '@heroicons/react/16/solid';
import { RadioCheckedIcon } from '@components/common/Icons';
import {
  ListboxStyled, ListboxStyledButton, ListboxStyledOptions, ListboxStyledOption, Chip,
} from '@components/common';
import { ListboxWrapper, ListboxWrapperProps } from '@components/common/Forms';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { dataTestId } from '@services/data-test-id';
import { sortBy } from '../helpers/filterObjects';
import { filterStyles } from '../helpers/filterStyles';


// Interfaces
// **************************************
type PickWrapperProps = 'recordFunc' | 'submitFunc' | 'idPrefix';
type OrderByGroupProps<DataType> = Pick<ListboxWrapperProps<DataType>, PickWrapperProps> & {
  jobSlug?: string;
};


/**
 * @description The order by group component. It's a stylized ListboxWrapper component for sorting the results.
 * ***recordFunc** - the function to record the selected value.
 * ***submitFunc** - the function to submit the selected value.
 * ***idPrefix** - the prefix for the id of the component. Defaults to an empty string.
 */
export const OrderByGroup = <DataType extends object>(props: OrderByGroupProps<DataType>) => {
  // Destruction props
  const {
    recordFunc,
    submitFunc,
    idPrefix,
    jobSlug,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Add sort by application date if in job context (jobSlug is provided)
  const fields = useMemo(() => {
    if (jobSlug) {
      return sortBy.fields.concat({
        label: 'order-by-application-date',
        recordOptions: {
          defaultValue: 'active',
          filtersLabel: 'order-by-application-date',
        },
      });
    }
    return sortBy.fields;
  }, [jobSlug]);


  // Render component
  // **********************************************
  return (
    <ListboxWrapper
      fieldName="sort"
      data={fields}
      defaultValueKey="recordOptions.defaultValue"
      labelKey="recordOptions.filtersLabel"
      withTranslation
      recordFunc={recordFunc}
      submitFunc={submitFunc}
      idPrefix={idPrefix}
      renderListbox={({
        selectedLabel, lsData, selected, setSelected,
      }) => (
        <ListboxStyled
          className="relative"
          by="label"
          value={selected as (typeof fields)[number]}
          onChange={setSelected}
        >
          {({ open }) => (
            <>
              <ListboxStyledButton
                as={Chip}
                icon={<ArrowsUpDownIcon />}
                isDropdownOpen={open}
                withDropdownArrow
              >
                {selectedLabel}
              </ListboxStyledButton>

              <ListboxStyledOptions
                className={`${filterStyles.listboxOptionsWrapper} pb-2`}
                animation="slideDown"
                anchor="bottom start"
              >
                <span className="my-2 block px-3.5 text-sm text-ink-light">{`${t('order-by')}:`}</span>
                {lsData.map((field) => (
                  <ListboxStyledOption
                    key={field.label}
                    value={field}
                    className={`${filterStyles.listboxOption} gap-1.5`}
                    {...dataTestId(`cvSearchPage.${field.label as 'orderByRelevance' | 'orderByDistance' | 'orderByActivity'}`)}
                  >
                    <RadioCheckedIcon className={filterStyles.listboxOptionCheck} />
                    {t(field.label)}
                  </ListboxStyledOption>
                ))}
              </ListboxStyledOptions>

            </>
          )}
        </ListboxStyled>
      )}
    />
  );
};
